import React from 'react';
import { X } from 'lucide-react';
import { useAuditLog } from '../../hooks/useAuditLog';
import { EntityType, AuditLog } from '../../types';
import AuditLogItem from './AuditLogItem';
import LoadingSpinner from '../common/LoadingSpinner';

interface AuditDrawerProps {
  entityType: EntityType;
  entityId: string;
  entityName?: string;
  isOpen: boolean;
  onClose: () => void;
}

const AuditDrawer: React.FC<AuditDrawerProps> = ({
  entityType,
  entityId,
  entityName,
  isOpen,
  onClose,
}) => {
  const { data: auditLogs, isLoading, error } = useAuditLog(entityType, entityId);

  if (!isOpen) return null;

  const getEntityTypeLabel = (type: EntityType): string => {
    const labels: Record<EntityType, string> = {
      COMPANY: 'Empresa',
      USER: 'Usuario',
      PROJECT: 'Proyecto',
      CATEGORY: 'Categoría',
      SUBCATEGORY: 'Subcategoría',
      EXPENSE: 'Gasto',
      ATTACHMENT: 'Adjunto',
      AUDIT_LOG: 'Log de Auditoría',
      INVOICE: 'Factura',
      ASSET: 'Activo',
      ASSET_TYPE: 'Tipo de Activo',
      CLIENT: 'Cliente',
    };
    return labels[type] || type;
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Historial {entityName && `- ${entityName}`}
              </h2>
              <p className="text-sm text-gray-500">
                {getEntityTypeLabel(entityType)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {isLoading && (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            )}

            {!!error && (
              <div className="text-center py-8">
                <p className="text-red-600">Error al cargar el historial</p>
              </div>
            )}

            {!isLoading && !error && auditLogs && auditLogs.length === 0 && (
              <div className="text-center py-8">
                <svg
                  className="w-12 h-12 text-gray-400 mx-auto mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-gray-600">No hay historial disponible</p>
              </div>
            )}

            {!isLoading && !error && auditLogs && auditLogs.length > 0 && (
              <div className="space-y-4">
                {auditLogs.map((log: AuditLog) => (
                  <AuditLogItem key={log.auditId} log={log} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AuditDrawer;
